import { Dispatch, SetStateAction, useState } from "react";
import { fileWrapper, folderWrapper } from "./NewFilesUploader";
import {
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/solid";
import { TextInput } from "../../tailwind/TextInput";
import { formatFileSize } from "@/src/utils/formatFileSize";
import { DataRoomPermission } from "@/src/graphql/generated";
import Dropdown from "../../tailwind/Dropdown";
import { LocalFileIcon } from "../../icons/LocalFileIcon";
import { FileWrapStatus } from "./fileWrapStatus";
import clsx from "clsx";
import { updateFolderRecursively } from "./MultiStepUploader";
import { ProgressBar } from "../../ProgressBar";

// Helper to get all files from a folder recursively (including nested subfolders)
function getAllFilesFromFolder(folder: folderWrapper): fileWrapper[] {
  const files: fileWrapper[] = [...(folder.files ?? [])];
  for (const child of folder.children ?? []) {
    files.push(...getAllFilesFromFolder(child));
  }
  return files;
}

// Calculate cumulative upload progress for all files in a folder
function getFolderUploadProgress(folder: folderWrapper): {
  progress: number;
  isUploading: boolean;
  isComplete: boolean;
} {
  const allFiles = getAllFilesFromFolder(folder);

  if (allFiles.length === 0) {
    return {
      progress: 0,
      isUploading: false,
      isComplete: folder.status === "complete",
    };
  }

  const isUploading = allFiles.some(
    (f) => f.status === "uploading" || f.status === "creating",
  );
  const isComplete = allFiles.every(
    (f) => f.status === "complete" || f.createdDataRoomFileId,
  );

  // Calculate average progress across all files
  const totalProgress = allFiles.reduce((sum, file) => {
    if (file.status === "complete" || file.createdDataRoomFileId) {
      return sum + 100;
    }
    return sum + (file.uploadProgress ?? 0);
  }, 0);

  const progress = totalProgress / allFiles.length;

  return { progress, isUploading, isComplete };
}
interface LocalFolderItemProps {
  folder: folderWrapper;
  permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  onFoldersChange: Dispatch<SetStateAction<folderWrapper[]>>;
  onFilesChange: Dispatch<SetStateAction<fileWrapper[]>>;
  depth: number;
}

export function LocalFolderItem(props: LocalFolderItemProps) {
  const { folder, onFoldersChange, onFilesChange, permissions } = props;
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  // Get cumulative upload progress for this folder
  const { progress, isUploading, isComplete } = getFolderUploadProgress(folder);

  return (
    <div
      className={clsx(
        "relative flex flex-col py-3 my-2 gap-x-2",
        props.depth === 0 ? " border-b border-gray-200" : "",
      )}
    >
      <div className="flex items-center justify-between group">
        <div className="flex items-center">
          <button onClick={toggleExpand} className="mr-2">
            {isExpanded ? (
              <ChevronDownIcon className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRightIcon className="w-5 h-5 text-gray-500" />
            )}
          </button>
          <FolderIcon className="w-6 h-6 text-blue-500" />
          <div className="ml-3 flex items-center gap-x-1.5">
            {folder.status === "pending" ? (
              <div className="w-64">
                <TextInput
                  value={folder.name}
                  inputSize="s"
                  onChange={(e) => {
                    const value = e.currentTarget.value;

                    onFoldersChange((prevFolders) =>
                      updateFolderRecursively(
                        prevFolders,
                        props.folder.id,
                        (folder) => ({
                          ...folder,
                          name: value,
                        }),
                      ),
                    );
                  }}
                />
              </div>
            ) : (
              <p className="font-semibold text-sm text-gray-700 truncate">
                {folder.name}
              </p>
            )}
            <p className="font-semibold text-sm text-gray-700 truncate inline-block">
              <span className="text-xs text-gray-500  font-normal">
                {folder.files && folder.files.length === 1
                  ? `1 document`
                  : folder.files && folder.files?.length > 1
                    ? `${folder.files.length} documents`
                    : "No documents"}
                {folder.children && folder.children.length > 0
                  ? `, ${folder.children.length} subfolder(s)`
                  : ""}
              </span>
            </p>
          </div>
        </div>
        {props.depth === 0 ? (
          <div className="flex items-center gap-x-2">
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500 hidden group-hover:block transition-all"
              onClick={() =>
                onFoldersChange((prev) =>
                  prev.filter((f) => f.id !== folder.id),
                )
              }
              title="Remove folder"
            >
              {folder.status === "pending" && (
                <TrashIcon className="w-5 h-5 text-gray-500/80 hover:text-gray-700 cursor-pointer opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out" />
              )}
            </button>
            {isComplete ? (
              <div className="flex items-center gap-x-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
              </div>
            ) : isUploading ? (
              <div className="flex items-center gap-x-2 w-40">
                <ProgressBar height="h-2" progress={progress} />
              </div>
            ) : (
              <Dropdown
                hideSelectedOptionDescription
                variant="light"
                selectedOption={folder.selectedPermission}
                onSelect={(option) => {
                  onFoldersChange((prevFolders) =>
                    updateFolderRecursively(
                      prevFolders,
                      props.folder.id,
                      (folder) => {
                        // Helper to recursively update all nested children
                        const updateChildrenPermissions = (
                          children: folderWrapper[] | undefined,
                        ): folderWrapper[] | undefined => {
                          if (!children) return children;
                          return children.map((child) => ({
                            ...child,
                            selectedPermission: option,
                            files: child.files?.map((f) => ({
                              ...f,
                              selectedPermission: option,
                            })),
                            children: updateChildrenPermissions(child.children),
                          }));
                        };

                        return {
                          ...folder,
                          selectedPermission: option,
                          files: folder.files?.map((x) => ({
                            ...x,
                            selectedPermission: option,
                          })),
                          children: updateChildrenPermissions(folder.children),
                        };
                      },
                    ),
                  );
                }}
                options={permissions.map((permission) => ({
                  value: permission.id,
                  label: permission.name,
                  description: permission.description,
                }))}
              />
            )}
          </div>
        ) : null}
      </div>
      {isExpanded && (
        <div className="ml-10 mt-2 border-l border-gray-300 pl-4">
          {folder.files?.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between py-1 gap-x-8 group"
            >
              <div className="flex flex-1 items-center">
                <div className="mr-2">
                  <LocalFileIcon
                    fileType={file.file.type}
                    fileName={file.file.name}
                  />
                </div>
                <div className="flex flex-1 items-center">
                  <TextInput
                    className="flex-1 max-w-md"
                    value={file.name}
                    inputSize="s"
                    onChange={(e) => {
                      const value = e.currentTarget.value;

                      onFoldersChange((prevFolders) =>
                        updateFolderRecursively(
                          prevFolders,
                          props.folder.id,
                          (folder) => ({
                            ...folder,
                            files: folder.files?.map((x) =>
                              x.id === file.id ? { ...x, name: value } : x,
                            ),
                          }),
                        ),
                      );
                    }}
                  />
                  <span className="text-xs text-gray-500 ml-2">
                    ({formatFileSize(file.file.size)})
                  </span>
                </div>
              </div>
              {file.status === "pending" ? (
                <>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 hidden group-hover:block transition-all"
                    onClick={() => {
                      onFoldersChange((prevFolders) =>
                        updateFolderRecursively(
                          prevFolders,
                          props.folder.id,
                          (folder) => ({
                            ...folder,
                            files: folder.files?.filter(
                              (x) => x.id !== file.id,
                            ),
                          }),
                        ),
                      );
                    }}
                    title="Remove file"
                  >
                    {folder.status === "pending" && (
                      <TrashIcon className="w-5 h-5 text-gray-500/80 hover:text-gray-700 cursor-pointer opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out" />
                    )}
                  </button>
                </>
              ) : (
                <FileWrapStatus fileWrap={file} onRetry={() => {}} />
              )}
            </div>
          ))}
          {folder.children &&
            folder.children.map((childFolder) => (
              <LocalFolderItem
                key={childFolder.id}
                folder={childFolder}
                permissions={permissions}
                onFoldersChange={onFoldersChange}
                onFilesChange={onFilesChange}
                depth={props.depth + 1}
              />
            ))}
        </div>
      )}
    </div>
  );
}
